import { TimelineSpec, parseTimeline } from "./timelineSpec";

/**
 * Archi draws by writing a fenced ```archi-diagram block holding this spec;
 * the panel lays it out (dagre) and renders it (ReactFlow). How to write it is
 * in Archi's system prompt (customer-api-docs `apps/archi/src/prompt.ts`): the
 * two shapes must stay in step.
 *
 * A drawing is either what exists — every element read from the user's data —
 * or a scenario: what exists plus `planned` elements Archi proposes. Planned
 * elements are drawn dashed and labelled, so a proposal is never mistaken for
 * the network as it is.
 */
export type DiagramNodeKind =
  | "customer"
  | "site"
  | "datacenter"
  | "port"
  | "cloud"
  | "backbone"
  | "internet"
  | "dci"
  | "sdwan"
  | "firewall"
  | "service"
  // CTX Reflector (apps/archi `draw_reflectors`): a BGP peer, a reflector,
  // and the transit gateway it is attached to. Its VPCs are drawn as "cloud".
  | "peer"
  | "reflector"
  | "transit_gateway";

export type DiagramStatus = "up" | "down" | "degraded" | "unknown";

export interface DiagramNode {
  id: string;
  label: string;
  kind: DiagramNodeKind;
  detail?: string;
  status?: DiagramStatus;
  /** Proposed by Archi, not in the user's network. */
  planned?: boolean;
  /** The service's subscription id ("10-7E2C9A") when the element is one service: what a click opens. */
  ref?: string;
  /** What the answer points at — failing, degraded, unread, affected: drawn to catch the eye. */
  focus?: boolean;
}

export interface DiagramEdge {
  from: string;
  to: string;
  label?: string;
  status?: DiagramStatus;
  planned?: boolean;
  focus?: boolean;
}

export interface DiagramSpec {
  title?: string;
  /** "Proposed" when the drawing carries planned elements; set by Archi or derived. */
  mode?: "current" | "scenario";
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

/** The blocks an answer can carry, each rendered as a card instead of text. */
const FENCES = {
  diagram: "```archi-diagram",
  timeline: "```archi-timeline",
} as const;

export type AnswerPart =
  | { kind: "text"; text: string }
  | { kind: "diagram"; spec: DiagramSpec | null }
  | { kind: "timeline"; spec: TimelineSpec | null };

/**
 * Splits an answer into text, drawings and timelines, in order. A block still
 * streaming, or one that does not parse, comes back as `spec: null` (a
 * placeholder while it arrives).
 */
export function splitAnswer(text: string): AnswerPart[] {
  const parts: AnswerPart[] = [];
  let rest = text;
  for (;;) {
    // The next block of either kind, whichever comes first.
    const next = (Object.keys(FENCES) as (keyof typeof FENCES)[])
      .map((kind) => ({ kind, start: rest.indexOf(FENCES[kind]) }))
      .filter((f) => f.start !== -1)
      .sort((a, b) => a.start - b.start)[0];
    if (!next) {
      if (rest.trim()) parts.push({ kind: "text", text: rest });
      return parts;
    }
    if (rest.slice(0, next.start).trim())
      parts.push({ kind: "text", text: rest.slice(0, next.start) });
    const body = rest.slice(next.start + FENCES[next.kind].length);
    const end = body.indexOf("```");
    const raw = end === -1 ? null : body.slice(0, end);
    if (next.kind === "diagram")
      parts.push({
        kind: "diagram",
        spec: raw === null ? null : parseSpec(raw),
      });
    else
      parts.push({
        kind: "timeline",
        spec: raw === null ? null : parseTimeline(raw),
      });
    if (end === -1) return parts;
    rest = body.slice(end + 3);
  }
}

export function parseSpec(raw: string): DiagramSpec | null {
  try {
    const spec = JSON.parse(raw.trim()) as DiagramSpec;
    if (!Array.isArray(spec.nodes) || !Array.isArray(spec.edges)) return null;
    const ids = new Set(spec.nodes.map((n) => n.id));
    // An edge to a node that is not drawn is dropped rather than drawn to nowhere.
    const edges = spec.edges.filter((e) => ids.has(e.from) && ids.has(e.to));
    const proposes =
      spec.nodes.some((n) => n.planned) || edges.some((e) => e.planned);
    return {
      ...spec,
      edges,
      mode: proposes ? "scenario" : (spec.mode ?? "current"),
    };
  } catch {
    return null;
  }
}
